// Shared saved ideas: cached pages stay anonymous; reviewing never creates a poll.
import { knownEmail, rememberEmail } from '~/javascripts/known-email.js';

(function () {
  if (window.TeamShortlist) return;
  const STORAGE = 'discoverShortlistSlugs', HINT = 'discoverShortlistHintDismissed', DISMISSED = 'discoverShortlistBarDismissed', PENDING = 'discoverShortlistPendingStates';
  const LIMIT = 24;
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const escape = value => String(value || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const normalize = value => Array.isArray(value) ? [...new Set(value.filter(s => typeof s === 'string' && s.trim() && s.length <= 250))].slice(0, LIMIT) : [];
  const read = (key, fallback) => { try { return localStorage.getItem(key) ?? fallback; } catch (_) { return fallback; } };
  const write = (key, value) => { try { localStorage.setItem(key, value); } catch (_) { /* In-memory state remains usable. */ } };
  let slugs = [];
  try { slugs = normalize(JSON.parse(read(STORAGE, '[]'))); } catch (_) { /* Malformed storage starts empty. */ }
  let dismissed = read(DISMISSED, '') === '1', hintDismissed = read(HINT, '') === '1';
  let authenticated = null, records = new Map(), selected = new Set(slugs);
  let loaded = false, loading = false, needsMerge = true, revision = 0, requestId = 0;
  let opener = null, submitting = false, syncRunning = false, initialized = false;
  let listResizeObserver = null;
  const dockRecords = new Map(), dockAttempted = new Set();
  let dockLoading = false;
  const pending = new Map();
  try {
    const stored = JSON.parse(read(PENDING, '[]'));
    if (Array.isArray(stored)) stored.slice(0, 100).forEach(pair => {
      if (Array.isArray(pair) && typeof pair[0] === 'string' && pair[0].length <= 250 && typeof pair[1] === 'boolean') pending.set(pair[0], pair[1]);
    });
  } catch (_) { /* Ignore malformed pending state. */ }

  function announce(message) {
    const inDialog = $('[data-saved-ideas-dialog]')?.open;
    const local = $('[data-shortlist-status]'), global = $('[data-shortlist-announcement]');
    if (local) local.textContent = inDialog ? message : '';
    if (global) global.textContent = inDialog ? '' : message;
  }
  function persist() { write(STORAGE, JSON.stringify(slugs)); }
  function persistPending() { write(PENDING, JSON.stringify([...pending])); }
  async function request(url, options) {
    const controller = typeof AbortController === 'undefined' ? null : new AbortController();
    const timeout = controller && setTimeout(() => controller.abort(), 10000);
    try { return await fetch(url, { ...options, signal: controller?.signal }); }
    finally { if (timeout) clearTimeout(timeout); }
  }
  function renderDock() {
    const stacks = $$('[data-shortlist-thumbnails]');
    if (!stacks.length) return;
    const previewSlugs = slugs.slice(0, 3);
    $$('[data-shortlist-idea-word]').forEach(node => { node.textContent = slugs.length === 1 ? 'idea' : 'ideas'; });
    $$('[data-shortlist-bar] .discover-shortlist-bar__saved').forEach(button => {
      button.setAttribute('aria-label', `Review your shortlist, ${slugs.length} good ${slugs.length === 1 ? 'idea' : 'ideas'}`);
    });
    const covers = previewSlugs.map(slug => ({ slug, image: (records.get(slug) || dockRecords.get(slug))?.image || '' }));
    const signature = JSON.stringify(covers);
    stacks.forEach(stack => {
      if (stack.dataset.covers === signature) return;
      stack.dataset.covers = signature;
      stack.innerHTML = covers.map(cover => `<span class="discover-shortlist-bar__thumbnail"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.5 5.5a5 5 0 0 0-7.1 0L12 7l-1.4-1.5a5 5 0 0 0-7.1 7.1L12 21l8.5-8.4a5 5 0 0 0 0-7.1Z"/></svg>${cover.image ? `<img src="${escape(cover.image)}" alt="" width="44" height="44" decoding="async">` : ''}</span>`).join('');
      $$('img', stack).forEach(image => {
        image.addEventListener('error', () => image.remove(), { once: true });
        if (image.complete && !image.naturalWidth) image.remove();
      });
    });
    // Resolve only visible dock covers, independently of poll/account state.
    // Initial hydration already loads their metadata; later saves need this
    // small read even when the saved-ideas dialog has never been opened.
    if (!initialized || loading || dockLoading || dismissed || !$('[data-shortlist-bar]:not([hidden])')) return;
    const missing = previewSlugs.filter(slug => !records.has(slug) && !dockRecords.has(slug) && !dockAttempted.has(slug));
    if (!missing.length) return;
    dockLoading = true;
    missing.forEach(slug => dockAttempted.add(slug));
    const query = new URLSearchParams();
    missing.forEach(slug => query.append('event_slugs[]', slug));
    request(`/shortlists/selection?${query}`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(response => { if (!response.ok) throw new Error('Cover metadata unavailable'); return response.json(); })
      .then(data => {
        if (!Array.isArray(data.events)) return;
        data.events.filter(event => event && missing.includes(event.slug)).forEach(event => dockRecords.set(event.slug, event));
        // Keep this image-only cache bounded to the current shortlist.
        for (const slug of dockRecords.keys()) if (!slugs.includes(slug)) dockRecords.delete(slug);
      })
      .catch(() => { /* Keep a deliberate cover placeholder; saves still work. */ })
      .finally(() => { dockLoading = false; renderDock(); });
  }
  function updateSubmit() {
    const count = slugs.filter(slug => selected.has(slug) && records.has(slug)).length;
    const button = $('[data-shortlist-submit]');
    if (button) {
      button.disabled = submitting || loading || !loaded || count === 0;
      button.textContent = submitting ? 'Creating your poll…' : `Create team poll${count ? ` (${count})` : ''}`;
    }
    const email = $('[data-shortlist-email]');
    if (email) {
      email.disabled = authenticated === true;
      email.required = authenticated !== true;
      $('[data-shortlist-email-step]').hidden = authenticated === true;
    }
  }
  function sync() {
    $$('[data-shortlist-count]').forEach(node => { node.textContent = slugs.length; node.hidden = node.classList.contains('ds-site-header__shortlist-count') && slugs.length === 0; });
    $$('[data-shortlist-open]').filter(node => !node.closest('[data-shortlist-bar]')).forEach(node => node.setAttribute('aria-label', `Open shortlist, ${slugs.length} saved activities`));
    $$('.ev-heart input').forEach(input => { input.checked = slugs.includes(input.value); });
    $$('[data-shortlist-heart]').forEach(button => {
      const active = slugs.includes(button.dataset.eventSlug);
      button.classList.toggle('ds-event-card__heart--active', active);
      button.classList.toggle('is-saved', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', `${active ? 'Remove from' : 'Save to'} your shortlist`);
      button.title = active ? 'Saved to your shortlist' : 'Save to your shortlist';
      const icon = $('svg', button);
      icon?.classList.toggle('is-filled', active);
      icon?.classList.toggle('text-brand-pink', active);
    });
    $$('[data-shortlist-hint]').forEach(node => { node.hidden = slugs.length > 0 || hintDismissed; });
    const onPoll = /^\/shortlists\/[^/]+\/?$/.test(location.pathname);
    $$('[data-shortlist-bar]').forEach(bar => { bar.hidden = !slugs.length || dismissed || onPoll; });
    renderDock();
    updateSubmit();
  }
  async function flush() {
    if (authenticated !== true || syncRunning || !pending.size) return;
    syncRunning = true;
    try {
      while (pending.size) {
        const [slug, saved] = pending.entries().next().value;
        const response = await request('/favorites/state', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ event_slug: slug, saved })
        });
        // Removed/delisted activities must not block every later save.
        if (!response.ok && response.status !== 404) throw new Error('Favorite sync failed');
        if (pending.get(slug) === saved) { pending.delete(slug); persistPending(); }
      }
    } catch (_) {
      announce('Saved on this device. Account syncing is unavailable right now; your picks are still here.');
    } finally { syncRunning = false; }
  }
  function setSaved(slug, saved, options = {}) {
    if (typeof slug !== 'string' || !slug.trim() || slug.length > 250) return false;
    if (options.sync === false) { pending.delete(slug); persistPending(); }
    if (slugs.includes(slug) === saved) {
      // A legacy favorites page can confirm a removal before hydration has
      // populated this module. Record that intent even if the local set agrees.
      if (options.sync !== false && authenticated !== false) {
        pending.set(slug, saved); persistPending(); revision += 1; flush();
      }
      return true;
    }
    if (saved && slugs.length >= LIMIT) {
      announce('Your shortlist has 24 activities. Remove one before saving another.');
      if (window.displayToast) window.displayToast('info', 'You can save up to 24 activities. Open your shortlist to make room.');
      return false;
    }
    if (saved) {
      slugs.push(slug); selected.add(slug);
      dockAttempted.delete(slug);
      dismissed = false; write(DISMISSED, '0');
      hintDismissed = true; write(HINT, '1');
    } else { slugs = slugs.filter(value => value !== slug); selected.delete(slug); }
    revision += 1; persist(); sync();
    if (options.sync !== false && authenticated !== false) { pending.set(slug, saved); persistPending(); flush(); }
    announce(saved ? 'Activity saved to your shortlist.' : 'Activity removed from your shortlist.');
    if ($('[data-saved-ideas-dialog]')?.open) {
      if (saved && !records.has(slug)) loadSelection(); else renderIdeas();
    }
    return true;
  }
  async function loadSelection() {
    const id = ++requestId, atRevision = revision, requested = [...slugs], mergeFavorites = needsMerge;
    const query = new URLSearchParams();
    requested.forEach(slug => query.append('event_slugs[]', slug));
    if (mergeFavorites) query.set('include_favorites', '1');
    loading = true; updateSubmit();
    if (!loaded && $('[data-saved-ideas-dialog]')?.open) renderIdeas();
    try {
      const response = await request(`/shortlists/selection?${query}`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Selection unavailable');
      const data = await response.json();
      if (id !== requestId) return;
      if (atRevision !== revision) { loadSelection(); return; }
      if (!Array.isArray(data.events)) throw new Error('Invalid selection');
      authenticated = data.authenticated === true;
      records = new Map(data.events.filter(e => e && typeof e.slug === 'string').map(e => [e.slug, e]));
      const resolved = normalize(data.events.map(e => e.slug)).filter(slug => pending.get(slug) !== false);
      const additions = resolved.filter(slug => !slugs.includes(slug));
      slugs = resolved;
      selected = new Set([...selected].filter(slug => slugs.includes(slug)));
      additions.forEach(slug => selected.add(slug));
      persist(); loaded = true; loading = false; needsMerge = false;
      if (mergeFavorites && authenticated) {
        const accountSlugs = normalize(data.favorite_slugs);
        requested.filter(slug => slugs.includes(slug) && !accountSlugs.includes(slug)).forEach(slug => pending.set(slug, true));
      }
      requested.filter(slug => !records.has(slug) && pending.get(slug) === true).forEach(slug => pending.delete(slug));
      if (!authenticated) pending.clear();
      persistPending();
      sync();
      if ($('[data-saved-ideas-dialog]')?.open) renderIdeas();
      if (requested.some(slug => !records.has(slug))) announce('An activity is no longer available and was removed from this device’s shortlist.');
      flush();
    } catch (_) {
      if (id !== requestId) return;
      loading = false; loaded = false; updateSubmit();
      if ($('[data-saved-ideas-dialog]')?.open) renderIdeas(true);
    }
  }
  function updateListScrollCue() {
    const list = $('[data-saved-ideas-list]'), cue = $('[data-shortlist-scroll-cue]');
    if (!list || !cue) return;
    cue.hidden = list.scrollHeight <= list.clientHeight + 2;
    cue.textContent = list.scrollTop + list.clientHeight >= list.scrollHeight - 4 ? 'Scroll up ↑' : 'Scroll for more ↓';
  }
  function renderIdeas(failed = false) {
    const list = $('[data-saved-ideas-list]');
    if (!list) return;
    const scrollTop = list.scrollTop;
    const active = document.activeElement;
    const focusSlug = active?.closest('[data-idea-slug]')?.dataset.ideaSlug;
    const focusAction = active?.dataset.shortlistAction;
    const focusChoice = active?.hasAttribute('data-shortlist-choice');
    const focusLink = active?.tagName === 'A';
    if (failed) {
      list.innerHTML = '<li class="saved-ideas-empty"><p>We couldn’t load your activities. Your saved picks are still on this device.</p><button type="button" class="ds-btn ds-btn--outline-simple" data-shortlist-action="retry">Try again</button></li>';
      if (focusSlug) $('[data-shortlist-action="retry"]', list)?.focus({ preventScroll: true });
      updateListScrollCue();
      return;
    }
    if (!loaded) { list.innerHTML = '<li class="saved-ideas-loading" role="status">Loading your saved activities…</li>'; updateListScrollCue(); return; }
    if (!slugs.length) {
      list.innerHTML = '<li class="saved-ideas-empty"><span aria-hidden="true">♡</span><h3>Good ideas start with a heart.</h3><p>Save a few activities as you explore. Then bring them here and let your team vote.</p><a class="ds-btn ds-btn--primary" href="/activities">Explore activities</a></li>';
    } else {
      list.innerHTML = slugs.map((slug, index) => {
        const e = records.get(slug);
        if (!e) return '';
        return `<li class="saved-idea" data-idea-slug="${escape(slug)}">
          <label class="saved-idea-check"><input type="checkbox" data-shortlist-choice="${escape(slug)}" ${selected.has(slug) ? 'checked' : ''}><span class="saved-ideas-sr-only">Include ${escape(e.title)} in the team poll</span></label>
          ${e.image ? `<img class="saved-idea-photo" src="${escape(e.image)}" alt="" width="88" height="88" loading="lazy">` : '<span class="saved-idea-photo saved-idea-placeholder" aria-hidden="true">♡</span>'}
          <div class="saved-idea-copy"><h3><a href="${escape(e.url)}">${escape(e.title)}</a></h3><p class="saved-idea-meta">${[e.format, e.duration].filter(Boolean).map(escape).join(' · ')}</p><p class="saved-idea-summary">${escape(e.summary)}</p></div>
          <div class="saved-idea-tools"><button type="button" data-shortlist-action="up" ${index === 0 ? 'disabled' : ''} aria-label="Move ${escape(e.title)} earlier in your shortlist">↑</button><button type="button" data-shortlist-action="down" ${index === slugs.length - 1 ? 'disabled' : ''} aria-label="Move ${escape(e.title)} later in your shortlist">↓</button><button type="button" data-shortlist-action="remove" aria-label="Remove ${escape(e.title)} from your shortlist">×</button></div></li>`;
      }).join('');
    }
    $('[data-shortlist-poll-options]').hidden = !slugs.length;
    updateSubmit();
    list.scrollTop = scrollTop;
    updateListScrollCue();
    if (focusSlug) {
      const row = $$('[data-idea-slug]', list).find(node => node.dataset.ideaSlug === focusSlug);
      const target = row && (focusChoice ? $('[data-shortlist-choice]', row) : focusLink ? $('a', row) : focusAction ? $(`[data-shortlist-action="${focusAction}"]`, row) : null);
      if (target && !target.disabled) target.focus({ preventScroll: true });
      else $('[data-shortlist-close]')?.focus({ preventScroll: true });
    }
  }
  function focusListControl(target) {
    if (!target) return;
    target.focus({ preventScroll: true });
    const list = target.closest('[data-saved-ideas-list]');
    if (!list) return;
    const bounds = list.getBoundingClientRect(), control = target.getBoundingClientRect();
    if (control.top < bounds.top + 4) list.scrollTop -= bounds.top + 4 - control.top;
    else if (control.bottom > bounds.bottom - 4) list.scrollTop += control.bottom - bounds.bottom + 4;
  }
  // The dialog has two steps: compose (choose ideas, email) and created (copy
  // the poll link). Reopening always starts back at compose.
  function showStep(created) {
    const dialog = $('[data-saved-ideas-dialog]');
    if (!dialog) return;
    const compose = $('[data-shortlist-compose]', dialog), done = $('[data-shortlist-created]', dialog);
    if (compose) compose.hidden = created;
    if (done) done.hidden = !created;
    const title = $('#saved-ideas-title', dialog);
    if (title) title.textContent = created ? 'Your team poll is ready' : 'Your shortlist';
  }
  function showCreated(data) {
    const dialog = $('[data-saved-ideas-dialog]');
    $('[data-shortlist-share-url]', dialog).value = data.url;
    $('[data-shortlist-poll-link]', dialog).href = data.path || data.url;
    const emailed = $('[data-shortlist-emailed]', dialog);
    emailed.textContent = data.emailed_to
      ? (data.verification_required
        ? `Check ${data.emailed_to} for a confirmation email to receive your poll link and results by email.`
        : `We’ll email it to ${data.emailed_to} and send the results once your team has voted.`)
      : '';
    $('[data-shortlist-copy]', dialog).textContent = 'Copy link';
    showStep(true);
    announce('Your team poll is ready. Copy the link to share it.');
    $('[data-shortlist-copy]', dialog)?.focus();
  }
  async function copyShareLink(button) {
    const input = $('[data-shortlist-share-url]');
    if (!input?.value) return;
    let copied = false;
    try { await navigator.clipboard.writeText(input.value); copied = true; } catch (_) {
      input.select();
      try { copied = document.execCommand('copy'); } catch (__) { copied = false; }
    }
    button.textContent = copied ? 'Copied!' : 'Press ⌘C to copy';
    if (copied) announce('Poll link copied.');
    setTimeout(() => { button.textContent = 'Copy link'; }, 2000);
  }
  function open(trigger) {
    const dialog = $('[data-saved-ideas-dialog]');
    if (!dialog) { location.href = '/activities'; return; }
    showStep(false);
    opener = trigger || document.activeElement;
    selected = new Set(slugs);
    const email = $('[data-shortlist-email]');
    if (email && !email.value) email.value = knownEmail();
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('saved-ideas-open');
    renderIdeas(); $('[data-shortlist-close]', dialog)?.focus(); loadSelection();
  }
  function close() { $('[data-saved-ideas-dialog]')?.close(); }
  function dismissDock(trigger) {
    dismissed = true; write(DISMISSED, '1'); sync();
    if (document.activeElement === trigger) $$('[data-shortlist-open]').find(node => !node.closest('[data-shortlist-bar]') && node.getClientRects().length)?.focus({ preventScroll: true });
  }
  document.addEventListener('click', event => {
    const copyButton = event.target.closest('[data-shortlist-copy]');
    if (copyButton) { event.preventDefault(); copyShareLink(copyButton); return; }
    const button = event.target.closest('[data-shortlist-open],[data-shortlist-heart],[data-shortlist-close],[data-shortlist-dismiss],[data-shortlist-hint-dismiss],[data-shortlist-action]');
    if (!button) return;
    if (button.tagName === 'A' && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    event.preventDefault();
    if (button.hasAttribute('data-shortlist-open')) return open(button);
    if (button.hasAttribute('data-shortlist-close')) return close();
    if (button.hasAttribute('data-shortlist-dismiss')) return dismissDock(button);
    if (button.hasAttribute('data-shortlist-hint-dismiss')) { hintDismissed = true; write(HINT, '1'); sync(); return; }
    if (button.hasAttribute('data-shortlist-heart')) {
      const slug = button.dataset.eventSlug, adding = !slugs.includes(slug);
      if (setSaved(slug, adding) && adding && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        button.classList.remove('ds-anim-pop'); void button.offsetWidth; button.classList.add('ds-anim-pop');
        button.addEventListener('animationend', () => button.classList.remove('ds-anim-pop'), { once: true });
      }
      return;
    }
    const action = button.dataset.shortlistAction;
    if (action === 'retry') { loadSelection(); return; }
    const slug = button.closest('[data-idea-slug]')?.dataset.ideaSlug, index = slugs.indexOf(slug);
    if (index < 0) return;
    if (action === 'remove') {
      setSaved(slug, false);
      focusListControl($$('[data-shortlist-action="remove"]')[Math.min(index, slugs.length - 1)] || $('[data-shortlist-close]'));
    } else if (action === 'up' || action === 'down') {
      const target = index + (action === 'up' ? -1 : 1);
      if (target < 0 || target >= slugs.length) return;
      [slugs[index], slugs[target]] = [slugs[target], slugs[index]];
      revision += 1; persist(); renderIdeas(); renderDock();
      const row = $$('[data-idea-slug]').find(node => node.dataset.ideaSlug === slug);
      const focus = $(`[data-shortlist-action="${action}"]`, row);
      announce('Shortlist order updated. Your poll will use this order.');
      focusListControl(focus?.disabled ? $('[data-shortlist-action="remove"]', row) : focus);
    }
  });
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-shortlist-choice]')) return;
    const slug = event.target.dataset.shortlistChoice;
    if (event.target.checked) selected.add(slug); else selected.delete(slug);
    updateSubmit();
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches('[data-saved-ideas-form]')) return;
    const choices = slugs.filter(slug => selected.has(slug) && records.has(slug));
    if (submitting || loading || !loaded || !choices.length) { event.preventDefault(); return; }
    const inputs = $('[data-shortlist-slug-inputs]', form); inputs.replaceChildren();
    choices.forEach(slug => { const input = document.createElement('input'); input.type = 'hidden'; input.name = 'event_slugs[]'; input.value = slug; inputs.appendChild(input); });
    const email = $('[data-shortlist-email]', form);
    if (email && !email.disabled) rememberEmail(email.value);
    // Create in place so the organizer gets the link right here; fall back to
    // the normal form post (which lands on the poll page) if fetch fails.
    if (typeof fetch === 'undefined' || typeof FormData === 'undefined') { submitting = true; updateSubmit(); return; }
    event.preventDefault();
    submitting = true; updateSubmit(); announce('');
    request(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (response.ok && data.ok && data.url) { showCreated(data); return; }
        announce(data.error || 'We couldn’t create your poll. Please try again.');
      })
      .catch(() => announce('We couldn’t reach teambuilding.com. Check your connection and try again.'))
      .finally(() => { submitting = false; updateSubmit(); });
  });
  function init() {
    const dialog = $('[data-saved-ideas-dialog]');
    if (dialog && !dialog.dataset.shortlistBound) {
      dialog.dataset.shortlistBound = 'true';
      const list = $('[data-saved-ideas-list]', dialog);
      list?.addEventListener('scroll', updateListScrollCue, { passive: true });
      listResizeObserver?.disconnect();
      if (list && typeof ResizeObserver !== 'undefined') {
        listResizeObserver = new ResizeObserver(updateListScrollCue);
        listResizeObserver.observe(list);
      }
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
      });
      dialog.addEventListener('close', () => { document.documentElement.classList.remove('saved-ideas-open'); if (opener?.isConnected) opener.focus({ preventScroll: true }); });
    }
    submitting = false; sync();
    if (!initialized && (dialog || $('[data-shortlist-heart]'))) { initialized = true; loadSelection(); }
  }
  window.addEventListener('storage', event => {
    if (event.key === STORAGE) { try { slugs = normalize(JSON.parse(event.newValue || '[]')); } catch (_) { slugs = []; } selected = new Set(slugs); revision += 1; sync(); if ($('[data-saved-ideas-dialog]')?.open) loadSelection(); }
    if (event.key === DISMISSED) { dismissed = event.newValue === '1'; sync(); }
  });
  window.addEventListener('pageshow', () => { submitting = false; sync(); });
  window.addEventListener('resize', updateListScrollCue);
  window.addEventListener('online', () => { dockAttempted.clear(); renderDock(); flush(); if ($('[data-saved-ideas-dialog]')?.open) loadSelection(); });
  document.addEventListener('teambuilding:auth-state', event => {
    if (authenticated !== null && event.detail?.authenticated !== authenticated) { needsMerge = true; loadSelection(); }
  });
  ['DOMContentLoaded', 'turbo:load', 'turbolinks:load'].forEach(name => document.addEventListener(name, init));
  ['turbo:before-cache', 'turbolinks:before-cache'].forEach(name => document.addEventListener(name, close));
  window.syncDiscoverShortlistHearts = sync;
  window.TeamShortlist = { open, refresh: () => { needsMerge = true; return loadSelection(); }, add: (slug, options) => setSaved(slug, true, options), remove: (slug, options) => setSaved(slug, false, options), slugs: () => [...slugs] };
  if (document.readyState !== 'loading') init();
})();
